import { Component, Input, OnInit } from '@angular/core';
import { EnumHoldStatus, Hold } from 'src/app/state/user/holds/models/hold';
import { ApiService } from 'src/app/services/api.service';
import { Record } from 'src/app/models/record';
import { ButtonsConfiguration, HoldItemButtonConfiguration } from 'src/app/models/buttons-config';
import { Util } from 'src/app/util';
import { ConfigService } from '../../../services/config.service';
import { LoggerService } from 'src/app/services/logger.service';
import { RssItem } from 'src/app/models/rss';
import { PhysicalRecord } from 'src/app/models/physical-record';
import { DatePipe } from '@angular/common';
import { CardItemCover } from 'src/app/_shared/components/card-item/card-item.interface';
import { HoldsService } from 'src/app/state/user/holds/holds.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user-hold-item',
  templateUrl: './user-hold-item.component.html',
  styleUrls: ['./user-hold-item.component.scss'],
})
export class UserHoldItemComponent implements OnInit {
  @Input()
  hold: Hold;

  @Input()
  userId: string;

  record: Record;
  rssItem: RssItem;
  showButtons: boolean;
  buttonConfig: ButtonsConfiguration;
  isPhysicalItem: boolean;
  physicalRecord: PhysicalRecord;

  title: string;
  loaded = false;
  issueDate: string;

  cardItemDetails = [];
  coverDetails?: CardItemCover = undefined;

  constructor(
    private apiService: ApiService,
    private configService: ConfigService,
    private logger: LoggerService,
    private datePipe: DatePipe,
    private holdsService: HoldsService,
    private translate: TranslateService
  ) {
    this.buttonConfig = new HoldItemButtonConfiguration(this.configService.get().showReturnLoanButton);
  }

  ngOnInit() {
    this.showButtons = ['waiting', 'informed'].includes(this.hold.status);
    this.fillDetailsCardItem();
    this.apiService.getRecord(this.hold.parentRecordId).subscribe(
      (record) => {
        this.record = record;

        this.issueDate = Util.getLastIssueDate(record);
        this.isPhysicalItem = this.record.resourceTypes.includes('TIPO_FISICO');
        if (this.isPhysicalItem) {
          this.getPhysicalRecordInfo(this.record.id);
        }
        if (record.container) {
          if (!this.hold.rssRhId) {
            this.logger.warn('Hold comes with null recordHunk. Unable to render RSS item specific data');
            this.setRecordData(record);
            this.loaded = true;
          } else {
            this.apiService.getRssItem(record.id, this.hold.rssRhId).subscribe((rssItem) => {
              this.rssItem = rssItem;
              this.setRssItemData(rssItem);
              this.loaded = true;
            });
          }
        } else {
          this.setRecordData(record);
          this.loaded = true;
        }
      },
      (error) => {
        console.error(`Could not load record ${this.hold.recordId} (parent ${this.hold.parentRecordId})`, error);
      }
    );
  }

  private getPhysicalRecordInfo(recordId: string) {
    this.apiService.getPhysicalRecordInfo(recordId, 'PHYSICAL').subscribe((physicalRecordItem) => {
      const physicalItem = physicalRecordItem.generalInfoList;
      if (physicalItem && physicalItem.length === 1) {
        this.physicalRecord = physicalItem[0];
      }
    });
  }

  private fillDetailsCardItem() {
    /**
     * All hold statuses
     *  Hold available = HoldStatus.informed
     *  Hold accepted = HoldStatus.downloaded
     *  Hold waiting = HoldStatus.waiting
     *  Hold cancelled = HoldStatus.cancelled
     *  Hold expired = HoldStatus.expired
     *
     * In the cardItemDetails array, the data is added in the order in which it is to be displayed.
     */

    this.cardItemDetails = [];
    const availableDate: string = this.datePipe.transform(this.hold.availableUntilTime);

    switch (this.hold.status) {
      case EnumHoldStatus.informed:
        this.cardItemDetails.push({
          label: 'user.holds.statuses.available',
          value: availableDate,
        });
        break;
      case EnumHoldStatus.waiting:
        if (this.hold.format !== 'PHYSICAL') {
          this.holdsService.getWaitingTime(this.hold.id).subscribe((waitingTime) => {
            const label = waitingTime.waitingTime > 1 ? 'general.pluralDay' : 'general.singularDay';
            this.cardItemDetails.push({
              label: 'user.holds.statuses.waiting.time',
              value: `${waitingTime.waitingTime} ${this.translate.instant(label)}`,
            });
          });
        }
        break;
      case EnumHoldStatus.downloaded:
        this.cardItemDetails.push({
          label: 'user.holds.statuses.downloaded',
          value: this.datePipe.transform(this.hold.holdDate),
        });
        break;
      case EnumHoldStatus.expired:
      case EnumHoldStatus.cancelled:
        if (availableDate) {
          this.cardItemDetails.push({
            label: 'user.holds.statuses.expired',
            value: availableDate,
          });
        }
        break;
    }
  }

  private setRecordData(record: Record) {
    this.title = record.title;

    this.coverDetails = {
      url: record.coverImageUrl,
      icon: record.icon,
      isList: Boolean(record.wishListInfo),
    };
  }

  private setRssItemData(rssItem: RssItem) {
    Util.prepareRssItem(rssItem);
    this.title = rssItem.title;

    this.coverDetails = {
      url: rssItem.cover,
      icon: rssItem.icon,
      isList: Boolean(this.record.wishListInfo),
    };
  }
}
